'use client';

import { Toaster as Sonner, ToasterProps, toast as sonnerToast } from 'sonner';
import { cn } from '../lib/utils';
import { FaIcon } from './icon';

export function Toaster({ ...props }: ToasterProps) {
   return <Sonner className="toaster group" {...props} />;
}

export const toast = {
   dismiss: sonnerToast.dismiss,
   custom: (props: Omit<ToastProps, 'id'>) =>
      sonnerToast.custom(id => <Toast id={id} {...props} />),
   error: (title: string, options?: Omit<ToastProps, 'id' | 'title' | 'variant'>) =>
      sonnerToast.custom(id => <Toast id={id} title={title} variant="error" {...options} />, {
         duration: options?.duration,
      }),
   success: (title: string, options?: Omit<ToastProps, 'id' | 'title' | 'variant'>) =>
      sonnerToast.custom(id => <Toast id={id} title={title} variant="success" {...options} />, {
         duration: options?.duration,
      }),
   warning: (title: string, options?: Omit<ToastProps, 'id' | 'title' | 'variant'>) =>
      sonnerToast.custom(id => <Toast id={id} title={title} variant="warning" {...options} />, {
         duration: options?.duration,
      }),
};

interface ToastProps {
   id: string | number;
   title: string;
   description?: string;
   action?: {
      label: string;
      onClick: () => void;
   };
   cancel?: {
      label: string;
      onClick: () => void;
   };
   variant?: 'default' | 'error' | 'success' | 'warning';
   duration?: number;
   'data-testid'?: string;
}

function Toast({
   id,
   title,
   description,
   action,
   cancel,
   variant = 'default',
   'data-testid': dataTestId,
}: ToastProps) {
   return (
      <div
         className={cn(
            'flex w-full max-w-md rounded-xl border p-4 shadow-lg',
            variant === 'error' && 'bg-red-50 border-red-200',
            variant === 'success' && 'bg-green-50 border-green-200',
            variant === 'warning' && 'bg-amber-50 border-amber-200',
            variant === 'default' && 'bg-gray-50 border-gray-200'
         )}
         data-testid={dataTestId}
      >
         <div className="flex">
            <div className="flex-shrink-0">
               <FaIcon
                  name={
                     {
                        default: 'circle-info',
                        error: 'triangle-exclamation',
                        success: 'circle-check',
                        warning: 'triangle-exclamation',
                     }[variant]
                  }
                  style="solid"
                  className={cn(
                     'size-5',
                     variant === 'error' && 'text-red-500',
                     variant === 'success' && 'text-green-500',
                     variant === 'warning' && 'text-amber-500',
                     variant === 'default' && 'text-brand-500'
                  )}
               />
            </div>
            <div className="ml-3 flex-1">
               <p
                  className={cn(
                     'text-sm font-medium',
                     variant === 'error' && 'text-red-900',
                     variant === 'success' && 'text-green-900',
                     variant === 'warning' && 'text-amber-900',
                     variant === 'default' && 'text-gray-900'
                  )}
               >
                  {title}
               </p>
               {description && (
                  <p
                     className={cn(
                        'mt-1 text-sm',
                        variant === 'error' && 'text-red-800',
                        variant === 'success' && 'text-green-800',
                        variant === 'warning' && 'text-amber-800',
                        variant === 'default' && 'text-gray-700'
                     )}
                  >
                     {description}
                  </p>
               )}
               {(action || cancel) && (
                  <div className="mt-3 flex gap-2">
                     {action && (
                        <button
                           data-slot="button"
                           className={cn(
                              'button-xs',
                              variant === 'error' && 'bg-red-600 hover:bg-red-700',
                              variant === 'success' && 'bg-green-600 hover:bg-green-700',
                              variant === 'warning' && 'bg-amber-600 hover:bg-amber-700'
                           )}
                           onClick={() => {
                              action.onClick();
                              sonnerToast.dismiss(id);
                           }}
                        >
                           {action.label}
                        </button>
                     )}
                     {cancel && (
                        <button
                           data-slot="button"
                           className={cn(
                              'button-xs button-ghost',
                              variant === 'error' && 'text-red-700 hover:bg-red-100',
                              variant === 'success' && 'text-green-700 hover:bg-green-100',
                              variant === 'warning' && 'text-amber-700 hover:bg-amber-100'
                           )}
                           onClick={() => {
                              cancel.onClick();
                              sonnerToast.dismiss(id);
                           }}
                        >
                           {cancel.label}
                        </button>
                     )}
                  </div>
               )}
            </div>
         </div>
      </div>
   );
}
